import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, ExternalLink } from "lucide-react";

export const employeeNotices = {
  "employee-safety-health-english": {
    title: "Employee Safety and Health Protection",
    language: "English",
    description: "Official ADOSH employee safety and health protection poster.",
    imagePath: "/images/employee-notices/employee-safety-health-english.jpg",
  },
  "employee-safety-health-spanish": {
    title: "Protección de Seguridad y Sanidad para el Empleado",
    language: "Español",
    description: "Cartel oficial de ADOSH sobre protección de seguridad y sanidad.",
    imagePath: "/images/employee-notices/employee-safety-health-spanish.jpg",
  },
  "mrsa-meningitis-tb-exposure-english": {
    title: "MRSA, Spinal Meningitis, or TB Exposure",
    language: "English",
    description: "Official notice to employees for MRSA, spinal meningitis, and TB exposure.",
    imagePath: "/images/employee-notices/mrsa-meningitis-tb-exposure-english.jpg",
  },
  "bodily-fluids-exposure-english": {
    title: "Work Exposure to Bodily Fluids",
    language: "English",
    description: "Official notice to employees for HIV, AIDS, and Hepatitis C exposure.",
    imagePath: "/images/employee-notices/bodily-fluids-exposure-english.jpg",
  },
  "bodily-fluids-exposure-spanish": {
    title: "Exposición a Fluidos Corporales en el Trabajo",
    language: "Español",
    description: "Aviso oficial sobre exposición a fluidos corporales, VIH, SIDA y Hepatitis C.",
    imagePath: "/images/employee-notices/bodily-fluids-exposure-spanish.jpg",
  },
  "userra-poster": {
    title: "USERRA Rights Poster",
    language: "English",
    description: "Official notice of rights under the Uniformed Services Employment and Reemployment Rights Act.",
    filePath: "/documents/employee-notices/userra-poster.pdf",
    fileType: "pdf",
  },
};

const EmployeeNoticeViewer = () => {
  const { noticeId } = useParams();
  const notice = employeeNotices[noticeId];
  const assetPath = notice?.filePath || notice?.imagePath;
  const isPdf = notice?.fileType === "pdf";

  if (!notice) {
    return (
      <div className="min-h-screen bg-green-50 px-4 py-12">
        <div className="mx-auto max-w-3xl rounded-lg bg-white p-8 shadow-md">
          <h1 className="mb-4 text-2xl font-bold text-gray-900">Notice not found</h1>
          <p className="mb-6 text-gray-600">The requested employee notice could not be found.</p>
          <Link className="inline-flex items-center text-green-700 hover:text-green-800" to="/employee-portal">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Employee Portal
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-50 px-4 py-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-col gap-4 rounded-lg bg-white p-6 shadow-md md:flex-row md:items-center md:justify-between">
          <div>
            <Link className="mb-4 inline-flex items-center text-sm font-medium text-green-700 hover:text-green-800" to="/employee-portal">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Employee Portal
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">{notice.title}</h1>
            <p className="mt-2 text-gray-600">
              {notice.description} <span className="font-medium">({notice.language})</span>
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <a
              className="inline-flex items-center rounded-md border border-green-700 px-4 py-2 text-sm font-medium text-green-700 hover:bg-green-50"
              href={assetPath}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Open
            </a>
            <a
              className="inline-flex items-center rounded-md bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800"
              href={assetPath}
              download
            >
              <Download className="mr-2 h-4 w-4" />
              Download
            </a>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg bg-white shadow-md">
          {isPdf ? (
            <iframe
              className="h-[80vh] w-full bg-white"
              src={assetPath}
              title={notice.title}
            />
          ) : (
            <img
              alt={notice.title}
              className="mx-auto h-auto w-full max-w-5xl bg-white"
              src={assetPath}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeNoticeViewer;
